"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxPrimitive.Root> {
  size?: "sm" | "lg";
  label?: string;
  containerClassName?: string;
}

/**
 * Checkbox Component
 *
 * A custom-styled wrapper around Radix UI's CheckboxPrimitive.
 * Supports labeled checkboxes with configurable sizes.
 * Uses data attributes for interactive state styling (checked, unchecked, focus, disabled).
 */
function Checkbox({
  className,
  containerClassName,
  size = "sm",
  label,
  ...props
}: Readonly<CheckboxProps>) {
  return (
    <div className={cn(containerClassName, "flex items-center gap-1.5")}>
      <CheckboxPrimitive.Root
        data-slot="checkbox"
        className={cn(
          "peer flex items-center justify-center rounded-md border transition-all outline-none",
          "disabled:text-gray-light cursor-pointer disabled:cursor-not-allowed",

          // ✅ UNCHECKED STATE
          "data-[state=unchecked]:border-[var(--stroke-gray-normal)] data-[state=unchecked]:bg-transparent",
          "data-[state=unchecked]:hover:border-[var(--stroke-base-primary)]",
          "data-[state=unchecked]:focus:shadow-checkbox-active data-[state=unchecked]:focus:border-[var(--stroke-gray-normal)]",
          "data-[state=unchecked]:disabled:border-[var(--stroke-gray-normal)] data-[state=unchecked]:disabled:bg-[var(--fill-base-background)]",

          // ✅ CHECKED STATE
          "data-[state=checked]:bg-[var(--fill-base-main)] data-[state=checked]:text-white",
          "data-[state=checked]:hover:bg-[var(--fill-primary-solid-stronger)]",
          "data-[state=checked]:focus:shadow-checkbox-active data-[state=checked]:focus:bg-[var(--fill-base-main)]",
          "data-[state=checked]:disabled:bg-[var(--fill-gray-lighter)]",

          size === "sm" ? "h-5 w-5" : "h-6 w-6",
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="flex items-center justify-center text-current transition-none"
        >
          <CheckIcon className="size-3.5" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && (
        <label
          htmlFor={props.id}
          className={cn(
            "text-sm font-medium text-gray-800 peer-disabled:text-gray-400",
            size === "lg" ? "text-base" : "text-sm",
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
}

export { Checkbox };
